"use client";

import type { PortEvent } from "webmidi";

import { useTranslations } from "next-intl";
import { useCallback, useEffect } from "react";
import { useState } from "react";
import { useMemo } from "react";
import { logger } from "@utilities/logger";
import { WebMidi } from "webmidi";

import type { MidiDevice } from "../types";

import {
  midiDeviceConnected,
  midiDeviceDisconnected,
  midiDevicesDetected,
  midiDisabled,
  midiEnabled,
  midiSetActiveDevice,
  updateMidiMapping,
} from "../state/actions";
import { useSoundboard } from "../state/reducer";

export const useMidiConfig = () => {
  const { dispatch } = useSoundboard();
  const t = useTranslations("ClientKeys.soundboard.midi");

  const updateDeviceList = useCallback(() => {
    if (!WebMidi.enabled) return;

    const devices: MidiDevice[] = WebMidi.inputs.map(input => ({
      id: input.id,
      name: input.name || t("unknown_device"),
      manufacturer: input.manufacturer || t("unknown_manufacturer"),
      connection: input.connection,
      state: input.state,
      type: input.type,
    }));

    dispatch(midiDevicesDetected({ devices }));
  }, [dispatch, t]);

  const handleDeviceConnected = useCallback(
    (e: PortEvent) => {
      const device: MidiDevice = {
        id: e.port?.id || "",
        name: e.port?.name || t("unknown_device"),
        manufacturer: e.port?.manufacturer || undefined,
        connection: e.port?.connection || "",
        state: e.port?.state || "disconnected",
        type: e.port?.type || "input",
      };

      dispatch(midiDeviceConnected({ device }));
    },
    [dispatch, updateDeviceList, t],
  );

  const handleDeviceDisconnected = useCallback(
    (e: PortEvent) => {
      const device: MidiDevice = {
        id: e.port?.id || "",
        name: e.port?.name || t("unknown_device"),
        manufacturer: e.port?.manufacturer || undefined,
        connection: e.port?.connection || "",
        state: e.port?.state || "disconnected",
        type: e.port?.type || "input",
      };

      dispatch(midiDeviceDisconnected({ device }));
    },
    [dispatch, t],
  );

  const handleGrantPermissions = useCallback(() => {
    // Enable WebMidi
    WebMidi.enable()
      .then(() => {
        dispatch(midiEnabled());
        updateDeviceList();

        // Listen for device connection/disconnection
        WebMidi.addListener("connected", handleDeviceConnected);
        WebMidi.addListener("disconnected", handleDeviceDisconnected);

        // Clean up listeners
        return () => {
          WebMidi.removeListener("connected", handleDeviceConnected);
          WebMidi.removeListener("disconnected", handleDeviceDisconnected);
          // FIXME: should clean this up, but this caused the midi keyboard to stop working
          // WebMidi.disable();
        };
      })
      .catch(err => {
        logger.error(err instanceof Error ? err : null, "WebMidi could not be enabled");
        dispatch(midiDisabled({ error: err.message }));
        return;
      });
  }, [dispatch, handleDeviceConnected, handleDeviceDisconnected, updateDeviceList]);

  const handleDeviceChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const deviceId = e.target.value === "all" ? null : e.target.value;
      dispatch(midiSetActiveDevice({ deviceId }));
    },
    [dispatch],
  );

  useEffect(() => {
    if (WebMidi.supported) {
      navigator.permissions.query({ name: "midi" as PermissionName }).then(permission => {
        if (permission.state === "granted") {
          handleGrantPermissions();
        }
      });
    }
  }, []);

  return { handleGrantPermissions, handleDeviceChange };
};

export const MidiMappingConfigurator = ({ buttonIndex }: { buttonIndex: number }) => {
  const { state, dispatch } = useSoundboard();
  const [listeningForNote, setListeningForNote] = useState<null | number>(null);
  const { handleGrantPermissions, handleDeviceChange } = useMidiConfig();
  const t = useTranslations("ClientKeys.soundboard.midi");

  const existingMappedNote = useMemo(() => {
    const noteForButtonIndex = Object.entries(state.midi.mapping || {})
      .filter(([, mappedIndex]) => Number(mappedIndex) === buttonIndex)
      .map(([note]) => Number(note))
      .at(0);

    return noteForButtonIndex;
  }, [state.midi.mapping, buttonIndex]);

  useEffect(() => {
    setListeningForNote(null);
  }, [buttonIndex]);

  // Set up MIDI note listener for mapping
  useEffect(() => {
    if (!WebMidi.enabled || listeningForNote === null) return;

    const inputs = state.midi.activeDeviceId
      ? [WebMidi.getInputById(state.midi.activeDeviceId)].filter(Boolean)
      : WebMidi.inputs;

    const handleNoteOn = (e: any) => {
      const note = e.note.number;

      if (listeningForNote !== null) {
        // Update custom mapping
        dispatch(updateMidiMapping({ mapping: { ...state.midi.mapping, [note]: buttonIndex } }));

        // Stop listening after mapping
        setListeningForNote(null);
      }
    };

    inputs.forEach(input => {
      if (!input) return;
      input.addListener("noteon", handleNoteOn);
    });

    return () => {
      inputs.forEach(input => {
        if (!input) return;
        input.removeListener("noteon", handleNoteOn);
      });
    };
  }, [buttonIndex, dispatch, listeningForNote, state.midi.activeDeviceId, state.midi.enabled, state.midi.mapping]);

  // Helper to find which MIDI note is mapped to a button
  const getMidiNoteForButton = (button: number): string => {
    const midiNotes = Object.entries(state.midi.mapping || {})
      .filter(([, mappedIndex]) => Number(mappedIndex) === button)
      .map(([note]) => Number(note));

    if (midiNotes.length === 0) return t("none");

    // Convert MIDI note to name (C4, D#4, etc.)
    const noteNames = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
    const note = midiNotes[0];
    const octave = Math.floor(note / 12) - 1;
    const noteName = noteNames[note % 12];
    return `${noteName}${octave} (${note})`;
  };

  const clearMapping = useCallback(() => {
    if (!existingMappedNote) return;
    const newMapping = { ...state.midi.mapping };
    delete newMapping[existingMappedNote];
    dispatch(updateMidiMapping({ mapping: newMapping }));
  }, [existingMappedNote, state.midi.mapping, dispatch]);

  const handleStartMapping = useCallback(() => {
    setListeningForNote(buttonIndex);
  }, [buttonIndex]);

  if (!WebMidi.supported) {
    return null;
  }

  return (
    <div className='flex flex-col'>
      <span className='font-soundboard f-ui-01 mb-12 text-sm font-medium text-light'>{t("assign_midi")}</span>
      {!state.midi.enabled ? (
        <button
          className='font-soundboard f-ui-04 flex h-36 items-center justify-center rounded-sm border border-default px-12 py-4 transition-colors'
          onClick={handleGrantPermissions}
        >
          {t("grant_permissions")}
        </button>
      ) : (
        <>
          {state.midi.devices.length === 0 ? (
            <div className='text-yellow-400 text-xs'>{t("no_devices_found")}</div>
          ) : (
            <div className='rounded-sm border border-default px-4'>
              <select
                className='font-soundboard w-full text-sm'
                onChange={handleDeviceChange}
                value={state.midi.activeDeviceId || "all"}
              >
                {state.midi.devices.map(device => (
                  <option key={device.id} value={device.id}>
                    {device.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {state.midi.devices.length ? (
            <div className='my-12 flex flex-col'>
              {existingMappedNote !== null && existingMappedNote !== undefined && (
                <div className='font-soundboard f-ui-05 mb-12 flex items-center justify-between rounded-sm bg-dark px-12 py-8 text-sm'>
                  {t("mapped_to", { noteName: getMidiNoteForButton(buttonIndex) })}
                  <button className='font-soundboard f-ui-04 text-light' onClick={clearMapping}>
                    {t("clear")}
                  </button>
                </div>
              )}
              {listeningForNote !== null && (
                <button
                  className='font-soundboard f-ui-04 flex h-36 items-center justify-center rounded-sm border bg-inverse px-12 py-4 text-inverse transition-colors'
                  onClick={() => setListeningForNote(null)}
                >
                  {t("listening")}
                </button>
              )}
              {listeningForNote === null && (
                <button
                  className='font-soundboard f-ui-04 flex h-36 items-center justify-center rounded-sm border border-default px-12 py-4 transition-colors'
                  onClick={handleStartMapping}
                >
                  {t("configure_mapping")}
                </button>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
